import { supabase } from '../config/supabase';
import type { SupabaseTenantSitePageRow } from '../types/supabase';
import type {
  PublicTenantSitePageRecord,
  TenantSitePageContentMap,
  TenantSitePageKey,
  TenantSitePageRecord,
} from '../types/site-pages';

interface PublicTenantSitePageRow {
  clientId: string;
  clientName: string;
  clientSlug: string | null;
  pageKey: string;
  contentJson: Record<string, unknown> | null;
  hasPageContent: boolean | null;
  updatedAt: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeContentJson = <K extends TenantSitePageKey>(
  value: unknown,
): TenantSitePageContentMap[K] => {
  if (!isRecord(value)) {
    return {} as TenantSitePageContentMap[K];
  }

  return value as TenantSitePageContentMap[K];
};

const toTenantSitePageRecord = <K extends TenantSitePageKey>(
  row: SupabaseTenantSitePageRow,
  pageKey: K,
): TenantSitePageRecord<K> => ({
  clientId: row.clientId,
  pageKey,
  contentJson: normalizeContentJson<K>(row.contentJson),
  updatedByUserId: row.updatedByUserId ?? null,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
});

export class TenantSitePagesService {
  static async getByClientId<K extends TenantSitePageKey>(
    clientId: string,
    pageKey: K,
  ): Promise<TenantSitePageRecord<K> | null> {
    const { data, error } = await supabase
      .from('tenant_site_pages')
      .select('*')
      .eq('clientId', clientId)
      .eq('pageKey', pageKey)
      .maybeSingle();

    if (error) {
      console.error('Erro ao carregar tenant_site_pages:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    return toTenantSitePageRecord(data as SupabaseTenantSitePageRow, pageKey);
  }

  static async getAllByClientId(
    clientId: string,
  ): Promise<TenantSitePageRecord[]> {
    const { data, error } = await supabase
      .from('tenant_site_pages')
      .select('*')
      .eq('clientId', clientId)
      .order('pageKey', { ascending: true });

    if (error) {
      console.error('Erro ao listar tenant_site_pages:', error);
      throw error;
    }

    return ((data ?? []) as SupabaseTenantSitePageRow[]).map((row) =>
      toTenantSitePageRecord(row, row.pageKey),
    );
  }

  static async upsertForClient<K extends TenantSitePageKey>(
    clientId: string,
    pageKey: K,
    contentJson: TenantSitePageContentMap[K],
    updatedByUserId?: string | null,
  ): Promise<TenantSitePageRecord<K>> {
    const payload: Partial<SupabaseTenantSitePageRow> & {
      clientId: string;
      pageKey: K;
      contentJson: TenantSitePageContentMap[K];
    } = {
      clientId,
      pageKey,
      contentJson,
    };

    if (typeof updatedByUserId !== 'undefined') {
      payload.updatedByUserId = updatedByUserId;
    }

    const { data, error } = await supabase
      .from('tenant_site_pages')
      .upsert(payload, { onConflict: 'clientId,pageKey' })
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao salvar tenant_site_pages:', error);
      throw error;
    }

    return toTenantSitePageRecord(data as SupabaseTenantSitePageRow, pageKey);
  }

  static async getPublicBySlug<K extends TenantSitePageKey>(
    tenantSlug: string,
    pageKey: K,
  ): Promise<PublicTenantSitePageRecord<K> | null> {
    const { data, error } = await supabase.rpc('get_public_tenant_site_page_by_slug', {
      p_slug: tenantSlug,
      p_page_key: pageKey,
    });

    if (error) {
      console.error('Erro ao carregar tenant_site_pages publicamente:', error);
      throw error;
    }

    const row = (Array.isArray(data) ? data[0] : data) as PublicTenantSitePageRow | null;

    if (!row?.clientId) {
      return null;
    }

    const hasPageContent = Boolean(row.hasPageContent);

    return {
      clientId: row.clientId,
      clientName: row.clientName,
      clientSlug: row.clientSlug ?? null,
      pageKey,
      contentJson: normalizeContentJson<K>(row.contentJson),
      hasPageContent,
      updatedAt: row.updatedAt ?? null,
      source: hasPageContent ? 'db' : 'fallback',
    };
  }

  static async deleteForClient(
    clientId: string,
    pageKey: TenantSitePageKey,
  ): Promise<void> {
    const { error } = await supabase
      .from('tenant_site_pages')
      .delete()
      .eq('clientId', clientId)
      .eq('pageKey', pageKey);

    if (error) {
      console.error('Erro ao remover tenant_site_pages:', error);
      throw error;
    }
  }
}
